import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { onAnimationEnd } from '@theme/utilities';

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} [productImage] - Product image element
 */

/**
 * Standalone sticky add-to-cart bar for the PDP.
 * Observes the product-information shopify-section for DOM swaps and syncs from the live static form.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #visibilityObserver = null;

  /** @type {number | null} */
  #syncDebounceTimer = null;

  /** @type {number} */
  #syncGeneration = 0;

  /** @type {number} */
  #syncDebounceMs = 80;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {number | null} */
  #visibilityRafId = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {HTMLElement | null} */
  #stickyVisibilityTarget = null;

  /** @type {boolean} */
  #scrollListenersAttached = false;

  /** @type {HTMLElement | null} */
  #cachedProductSection = null;

  /** @type {HTMLElement | null} */
  #cachedDesktopSlot = null;

  /** @type {number | null} */
  #cachedHeaderOffset = null;

  /** @type {number} */
  #lastReconcileGeneration = -1;

  connectedCallback() {
    super.connectedCallback();

    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1', 10) || 1;
    this.#attachEventListeners();
    this.#attachScrollListeners();
    this.#syncFromDOM();
    this.#setupVisibility();
    requestAnimationFrame(() => requestAnimationFrame(this.#syncInitialBundleStickyState));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#visibilityObserver?.disconnect();
    this.#abortController.abort();
    if (this.#syncDebounceTimer) clearTimeout(this.#syncDebounceTimer);
    if (this.#visibilityRafId != null) cancelAnimationFrame(this.#visibilityRafId);
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);
  }

  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (this.dataset.needsSelection === 'true') {
      const productSection = this.#getProductSection();
      const bundlePicker = productSection?.querySelector('[data-bundle-picker]');
      if (bundlePicker instanceof HTMLElement) {
        bundlePicker.dispatchEvent(
          new CustomEvent('bundle:show-missing-option-errors', { bubbles: true })
        );
        return;
      }

      this.#scrollToVariantPicker();
      return;
    }

    this.#targetAddToCartButton = this.#resolveTargetAddToCartButton(this.#getProductForm());
    if (!this.#targetAddToCartButton) return;

    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();

    if (this.#isBundlePage()) {
      return;
    }

    const cartIcon = document.querySelector('.header-actions__cart-icon');
    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    const productImage = this.refs.productImage;
    if (!cartIcon || !this.refs.addToCartButton || !(productImage instanceof HTMLImageElement)) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    flyToCartElement.remove();
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  #attachEventListeners() {
    const { signal } = this.#abortController;

    document.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    document.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });
    document.addEventListener('variant-picker:state-sync', this.#handleVariantPickerStateSync, { signal });
    document.addEventListener('bundle:sticky-state', this.#handleBundleStickyState, { signal });
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
    document.addEventListener('piv2:pdp-sibling-hydrate-start', this.#handlePdpSiblingHydrateStart, { signal });
    document.addEventListener('piv2:pdp-sibling-hydrate-complete', this.#handlePdpSiblingHydrateComplete, {
      signal,
    });
  }

  /**
   * @param {CustomEvent} event
   */
  #handlePdpSiblingHydrateStart = (event) => {
    if (!this.#isEventInProductSection(event)) return;
    this.setAttribute('data-piv2-sibling-loading', 'true');
  };

  /**
   * @param {CustomEvent} event
   */
  #handlePdpSiblingHydrateComplete = (event) => {
    if (!this.#isEventInProductSection(event)) return;
    this.removeAttribute('data-piv2-sibling-loading');
    this.#scheduleDOMSync('pdp-sibling-hydrate-complete');
  };

  /**
   * @param {CustomEvent} event
   * @returns {boolean}
   */
  #isEventInProductSection(event) {
    const section = this.#getProductSection();
    if (!section) return false;
    const target = event.target;
    return target instanceof Node && section.contains(target);
  }

  #attachScrollListeners() {
    if (this.#scrollListenersAttached) return;

    const { signal } = this.#abortController;
    window.addEventListener('scroll', this.#scheduleStickyVisibilityUpdate, { passive: true, signal });
    window.addEventListener('resize', this.#handleResizeForSticky, { passive: true, signal });
    this.#scrollListenersAttached = true;
  }

  #handleResizeForSticky = () => {
    this.#cachedHeaderOffset = null;
    this.#scheduleStickyVisibilityUpdate();
  };

  /**
   * Debounced DOM sync — each call resets the timer so only the latest mutation batch runs.
   * @param {string} [source]
   */
  #scheduleDOMSync(source = 'unknown') {
    this.#syncGeneration += 1;
    const generation = this.#syncGeneration;

    this.#reconcileProductIdFromLiveDom();
    const scope = this.#getProductSection();
    if (scope) {
      this.#syncFromSource(scope, `${source}-eager`);
    }

    if (this.#syncDebounceTimer) clearTimeout(this.#syncDebounceTimer);

    this.#syncDebounceTimer = setTimeout(() => {
      this.#syncDebounceTimer = null;
      this.#flushDOMSync(generation, `${source}-settled`);
    }, this.#syncDebounceMs);
  }

  /**
   * @param {number} generation
   * @param {string} source
   */
  #flushDOMSync(generation, source) {
    if (generation !== this.#syncGeneration) return;

    requestAnimationFrame(() => {
      if (generation !== this.#syncGeneration) return;

      this.#invalidateCaches();
      this.#syncFromDOM(source);
      this.#setupVisibility();
    });
  }

  #invalidateCaches() {
    this.#cachedProductSection = null;
    this.#cachedDesktopSlot = null;
    this.#cachedHeaderOffset = null;
  }

  /**
   * @param {CustomEvent} event
   */
  #handleVariantUpdate = (event) => {
    const data = event.detail?.data;
    const eventProductId = String(data?.productId ?? '');
    const newProductId = String(data?.newProduct?.id ?? '');

    this.#reconcileProductIdFromLiveDom();
    const liveProductId = String(this.#findLiveProductForm()?.dataset.productId ?? '');
    const targetProductId = newProductId || eventProductId;
    const isRelevantUpdate =
      !targetProductId ||
      targetProductId === this.dataset.productId ||
      targetProductId === liveProductId ||
      Boolean(data?.html?.querySelector?.('[data-testid="product-information"], .product-information'));

    if (!isRelevantUpdate) return;

    if (targetProductId) {
      this.dataset.productId = targetProductId;
    }

    const html = data?.html;
    const variant = event.detail?.resource;
    const allOptionsSelected = this.#areAllOptionsSelected();
    const needsSelection = !allOptionsSelected || !variant?.id;
    const showPrice = !needsSelection;

    if (html instanceof Document || html instanceof Element) {
      this.#syncFromSource(html, 'variant-update-html');
      this.#syncPriceFromProductInformation(showPrice, html);
    }

    if (variant?.title) {
      this.#syncVariantTitleText(String(variant.title));
    }

    if (variant?.id) {
      this.dataset.currentVariantId = String(variant.id);
    } else if (variant == null) {
      this.#handleVariantUnavailable();
    }

    this.#scheduleDOMSync('variant-update');
  };

  /**
   * @param {CustomEvent} event
   */
  #handleVariantSelected = (event) => {
    const variantId = event.detail?.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = String(variantId);
  };

  /**
   * @param {CustomEvent} event
   */
  #handleVariantPickerStateSync = (event) => {
    const eventTarget = event?.target;
    if (
      eventTarget instanceof Element &&
      (eventTarget.closest('[data-bundle-picker]') || this.#isBundlePage())
    ) {
      return;
    }

    this.#reconcileProductIdFromLiveDom();
    this.#syncFromDOM('variant-picker-state-sync-immediate');
  };

  /**
   * @param {CustomEvent} _event
   */
  #handleCartAddComplete = (_event) => {
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
  };

  /**
   * @param {QuantitySelectorUpdateEvent} event
   */
  #handleQuantityUpdate = (event) => {
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * @param {CustomEvent} event
   */
  #handleBundleStickyState = (event) => {
    const loading = Boolean(event?.detail?.loading || event?.detail?.submitting);
    const ready = Boolean(event?.detail?.ready);
    const formattedTotal = typeof event?.detail?.formattedTotal === 'string' ? event.detail.formattedTotal : '';

    if (loading) {
      this.#applyBundleStickyLoadingState();
      return;
    }

    this.#applyBundleStickyReadyState(ready, formattedTotal);
  };

  #syncFromDOM(source = 'unknown') {
    this.#reconcileProductIdFromLiveDom();

    const scope = this.#getProductSection();
    if (!scope) return;

    this.#syncFromSource(scope, source);
    this.#syncStickyStateFromProductForm(source);
  }

  /**
   * @param {Document | Element} source
   * @param {string} [syncSource]
   */
  #syncFromSource(source, syncSource = 'unknown') {
    const scope = this.#resolveSyncScope(source);
    if (!scope) return;

    this.#syncTitleFromScope(scope);
    this.#syncImageFromScope(scope);
  }

  /**
   * @param {Document | Element} source
   * @returns {Element | null}
   */
  #resolveSyncScope(source) {
    if (source instanceof Document) {
      return (
        source.querySelector('[data-testid="product-information"]') ||
        source.querySelector('.product-information') ||
        source.querySelector('product-form-component')?.closest('.shopify-section') ||
        source.body ||
        source.documentElement
      );
    }

    if (source.matches('[data-testid="product-information"], .product-information, .shopify-section')) {
      return source;
    }

    return (
      source.querySelector('[data-testid="product-information"]') ||
      source.querySelector('.product-information') ||
      source
    );
  }

  /**
   * @param {Element} scope
   */
  #syncTitleFromScope(scope) {
    const titleSource =
      scope.querySelector('[ref="productTitleLink"] h1') ||
      scope.querySelector('[ref="productTitleLink"] .text-block') ||
      scope.querySelector('.product-details h1');

    const titleTarget = this.querySelector('[data-testid="sticky-product-title"]');
    if (!(titleSource instanceof HTMLElement) || !(titleTarget instanceof HTMLElement)) return;

    const nextTitle = titleSource.textContent?.trim();
    if (nextTitle) titleTarget.textContent = nextTitle;
  }

  /**
   * @param {Document | Element} [source]
   * @returns {HTMLElement | null}
   */
  #getProductInformationPriceContainer(source) {
    const root = source instanceof Document ? source : source ?? document;
    const productInfo =
      (root instanceof Document
        ? root.querySelector('[data-testid="product-information"]') || root.querySelector('.product-information')
        : null) ||
      (root instanceof Element && root.matches('[data-testid="product-information"], .product-information')
        ? root
        : null) ||
      (root instanceof Element
        ? root.querySelector('[data-testid="product-information"]') || root.querySelector('.product-information')
        : null);

    if (!(productInfo instanceof HTMLElement)) return null;

    const detailsSelector =
      window.innerWidth < 750
        ? '.product-information__details-mobile-body, .product-information__details-mobile-header'
        : '.product-information__details-desktop';

    const detailsRoot = productInfo.querySelector(detailsSelector);
    const priceContainer =
      detailsRoot?.querySelector('[ref="priceContainer"]') || productInfo.querySelector('[ref="priceContainer"]');

    return priceContainer instanceof HTMLElement ? priceContainer : null;
  }

  /**
   * @param {boolean} showPrice
   * @param {Document | Element} [source]
   */
  #syncPriceFromProductInformation(showPrice, source = document) {
    if (this.#isBundlePage()) return;

    const priceSource = this.#getProductInformationPriceContainer(source ?? document);
    const priceHtml = priceSource instanceof HTMLElement ? priceSource.innerHTML.trim() : '';
    const shouldShowPrice = showPrice && Boolean(priceHtml);
    const isMobileViewport = window.innerWidth < 1024;

    this.querySelectorAll('.sticky-add-to-cart__price').forEach((element) => {
      if (!(element instanceof HTMLElement)) return;
      if (element.dataset.bundleStickyPrice === 'true') return;

      const isBottomBarDesktopPrice = element.classList.contains('sticky-add-to-cart__price--desktop');

      if (isBottomBarDesktopPrice) {
        element.hidden = !shouldShowPrice || isMobileViewport;
        if (shouldShowPrice && !isMobileViewport && element.innerHTML !== priceHtml) {
          element.innerHTML = priceHtml;
        }
        return;
      }

      element.hidden = !shouldShowPrice;
      if (shouldShowPrice && element.innerHTML !== priceHtml) {
        element.innerHTML = priceHtml;
      }
    });
  }

  /**
   * @param {Element} scope
   */
  #syncImageFromScope(scope) {
    const productForm = this.#findProductFormInScope(scope);
    const addToCartComponent = productForm?.querySelector('add-to-cart-component');
    const mediaUrl =
      addToCartComponent instanceof HTMLElement ? addToCartComponent.dataset.productVariantMedia : '';

    const image = this.refs.productImage;
    if (image instanceof HTMLImageElement && mediaUrl) {
      image.src = mediaUrl;
      image.hidden = false;
    }
  }

  /**
   * @param {string} variantTitle
   */
  #syncVariantTitleText(variantTitle) {
    const variantTitleElement = this.querySelector('[data-testid="sticky-variant-title"]');
    if (!(variantTitleElement instanceof HTMLElement) || !variantTitle) return;

    variantTitleElement.textContent = variantTitle;
    variantTitleElement.style.display = '';
  }

  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('[data-testid="sticky-variant-title"]');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!(variantTitleElement instanceof HTMLElement) || !(variantPicker instanceof HTMLElement)) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');

    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
    variantTitleElement.style.display = '';
  };

  /**
   * @returns {HTMLElement | null}
   */
  #getVisibleVariantPicker() {
    const productSection = this.#getProductSection();
    if (!productSection) return null;

    const productId = this.dataset.productId;
    const pickers = Array.from(productSection.querySelectorAll('variant-picker')).filter((element) => {
      if (!(element instanceof HTMLElement)) return false;
      if (!productId) return true;
      return element.dataset.productId === productId || element.dataset.currentProductId === productId;
    });

    const visible = pickers.find((picker) => this.#isVisibleElement(picker)) ?? pickers[0];
    return visible instanceof HTMLElement ? visible : null;
  }

  /**
   * Reads checked option groups from the visible picker DOM (not dataset, which can lag after morph).
   * @returns {boolean}
   */
  #areAllOptionsSelected() {
    const picker = this.#getVisibleVariantPicker();
    if (!(picker instanceof HTMLElement)) return true;

    const fieldsets = picker.querySelectorAll('fieldset[data-option-name-handle]');
    const selects = picker.querySelectorAll('select.variant-option__select[data-option-name-handle]');
    const totalGroups = fieldsets.length + selects.length;
    if (totalGroups === 0) return true;

    let selectedCount = 0;

    fieldsets.forEach((fieldset) => {
      const handle = fieldset.getAttribute('data-option-name-handle');
      if (!handle) return;

      const checked =
        fieldset.querySelector('input[type="radio"]:checked') ||
        fieldset.querySelector('input[type="radio"][data-current-checked="true"]');
      if (checked instanceof HTMLInputElement && checked.value) selectedCount += 1;
    });

    selects.forEach((select) => {
      if (select instanceof HTMLSelectElement && select.value) selectedCount += 1;
    });

    return selectedCount >= totalGroups;
  }

  #syncStickyStateFromProductForm(source = 'unknown') {
    const productForm = this.#getProductForm();
    if (!(productForm instanceof HTMLElement)) return;

    const isBundlePage = this.#isBundlePage();
    if (isBundlePage) return;

    const variantIdInput = productForm.querySelector('[ref="variantId"]');
    const targetButton = this.#resolveTargetAddToCartButton(productForm);
    const variantId =
      variantIdInput instanceof HTMLInputElement && variantIdInput.value ? String(variantIdInput.value) : '';
    const allOptionsSelected = this.#areAllOptionsSelected();
    const needsSelection = !allOptionsSelected;
    const isButtonDisabled = targetButton instanceof HTMLButtonElement ? targetButton.disabled : true;
    const isUnavailable =
      targetButton instanceof HTMLButtonElement && targetButton.dataset.unavailable === 'true';

    this.#targetAddToCartButton = targetButton;
    this.dataset.currentVariantId = variantId;
    this.dataset.needsSelection = needsSelection ? 'true' : 'false';
    this.dataset.variantAvailable =
      !needsSelection && !isButtonDisabled && !isUnavailable ? 'true' : 'false';

    if (this.refs.addToCartButton instanceof HTMLButtonElement) {
      this.refs.addToCartButton.disabled = needsSelection ? false : isButtonDisabled || Boolean(isUnavailable);
      if (isUnavailable && !needsSelection) {
        this.refs.addToCartButton.dataset.unavailable = 'true';
      } else {
        delete this.refs.addToCartButton.dataset.unavailable;
      }
    }

    this.#ensureDesktopStickyButton();
    this.#syncDesktopStickyButtonState(needsSelection, isButtonDisabled, isUnavailable);

    const label = needsSelection ? 'Select options' : this.#resolveStickyButtonLabel(targetButton, false);

    this.#setStickyButtonLabel(label);
    this.#syncMobileButtonLabel(label, !needsSelection);
    this.#updateButtonText();

    const priceSource = this.#getProductInformationPriceContainer();
    const showPrice =
      !needsSelection &&
      priceSource instanceof HTMLElement &&
      !priceSource.hidden &&
      Boolean(priceSource.innerHTML.trim());
    this.#syncPriceFromProductInformation(showPrice);
  }

  /**
   * @param {HTMLButtonElement | null} targetButton
   * @param {boolean} needsSelection
   * @returns {string}
   */
  #resolveStickyButtonLabel(targetButton, needsSelection) {
    const staticLabel = targetButton?.querySelector('.add-to-cart-text__content-text')?.textContent?.trim();
    if (staticLabel) return staticLabel;

    const fallbackLabel = targetButton?.querySelector('.add-to-cart-text__content')?.textContent?.trim();
    if (fallbackLabel) return fallbackLabel;

    if (needsSelection) return 'Select options';

    return 'Add to bag';
  }

  /**
   * @returns {HTMLElement | null}
   */
  #ensureMobileButtonLabel() {
    const button = this.refs.addToCartButton;
    if (!(button instanceof HTMLButtonElement)) return null;

    const existingLabel = button.querySelector('.sticky-add-to-cart__button--mobile');
    if (existingLabel instanceof HTMLElement) return existingLabel;

    const mobileLabel = document.createElement('span');
    mobileLabel.className = 'sticky-add-to-cart__button--mobile';
    button.appendChild(mobileLabel);
    return mobileLabel;
  }

  /**
   * @param {string} label
   * @param {boolean} showMobileLabel
   */
  #syncMobileButtonLabel(label, showMobileLabel) {
    const mobileLabel = this.#ensureMobileButtonLabel();
    if (!(mobileLabel instanceof HTMLElement)) return;

    mobileLabel.textContent = label;
    if (window.innerWidth < 1024) {
      mobileLabel.hidden = false;
      return;
    }

    mobileLabel.hidden = !showMobileLabel;
  }

  #setupVisibility() {
    this.#visibilityObserver?.disconnect();
    this.#visibilityObserver = null;
    this.#invalidateCaches();

    const addToCartButton = this.#findStaticAddToCartButton();
    this.#targetAddToCartButton = this.#resolveTargetAddToCartButton(this.#getProductForm());

    if (addToCartButton instanceof HTMLElement) {
      this.#stickyVisibilityTarget = addToCartButton;
      this.#visibilityObserver = new IntersectionObserver(() => this.#scheduleStickyVisibilityUpdate(), { threshold: 0 });
      this.#visibilityObserver.observe(addToCartButton);
    } else {
      this.#stickyVisibilityTarget = null;
      this.refs.stickyBar.dataset.stuck = 'false';
      this.refs.stickyBar.dataset.productInfoOut = 'false';
    }

    this.refs.stickyBar.dataset.hiddenByBottom = 'false';
    this.#ensureDesktopStickyButton();
    requestAnimationFrame(() => this.#applyStickyVisibility());
  }

  #scheduleStickyVisibilityUpdate = () => {
    if (this.#visibilityRafId != null) return;

    this.#visibilityRafId = requestAnimationFrame(() => {
      this.#visibilityRafId = null;
      this.#applyStickyVisibility();
    });
  };

  #applyStickyVisibility = () => {
    if (!(this.#stickyVisibilityTarget instanceof HTMLElement)) {
      this.refs.stickyBar.dataset.stuck = 'false';
      this.refs.stickyBar.dataset.productInfoOut = 'false';
      this.#updateDesktopStickySlotVisibility(false);
      return;
    }

    const rect = this.#stickyVisibilityTarget.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) {
      this.refs.stickyBar.dataset.stuck = 'false';
      this.refs.stickyBar.dataset.productInfoOut = 'false';
      this.#updateDesktopStickySlotVisibility(false);
      return;
    }

    const headerOffset = this.#getHeaderOffset();
    const viewportHeight = window.innerHeight;
    const isOutAboveHeader = rect.bottom <= headerOffset;
    const isOutBelowViewport = rect.top >= viewportHeight;
    const isStaticInViewport = this.#isStaticInViewport(rect, headerOffset, viewportHeight);
    const isStaticBelowViewport = isOutBelowViewport;
    const isMobile = window.innerWidth < 750;
    const isDesktop = window.innerWidth >= 1024;
    const showMobileAbove = this.dataset.showMobileAbove === 'true';
    const isAtTop = window.scrollY <= 0;
    const isDesktopStickyEnabled = this.dataset.enableDesktopSticky === 'true';

    const shouldShowBottomBar =
      isOutAboveHeader ||
      (isMobile && showMobileAbove && isOutBelowViewport && !isAtTop);

    const showDesktopSlot =
      isDesktop && isDesktopStickyEnabled && isStaticBelowViewport && !isStaticInViewport;

    this.refs.stickyBar.dataset.stuck = shouldShowBottomBar ? 'true' : 'false';
    this.refs.stickyBar.dataset.productInfoOut = shouldShowBottomBar ? 'true' : 'false';

    if (isDesktop && isDesktopStickyEnabled) {
      this.#ensureDesktopStickyButton();
      this.#updateDesktopSlotGeometry();
      this.#updateDesktopStickySlotVisibility(showDesktopSlot);
    } else {
      this.#updateDesktopStickySlotVisibility(false);
    }
  };

  /**
   * @param {DOMRect} rect
   * @param {number} headerOffset
   * @param {number} viewportHeight
   * @returns {boolean}
   */
  #isStaticInViewport(rect, headerOffset, viewportHeight) {
    return rect.top < viewportHeight && rect.bottom > headerOffset;
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getDesktopStickySlot() {
    if (this.#cachedDesktopSlot?.isConnected) {
      return this.#cachedDesktopSlot;
    }

    const productSection = this.#getProductSection();
    this.#cachedDesktopSlot =
      productSection?.querySelector('.product-information__details-desktop [data-desktop-sticky-atc-slot]') ??
      null;

    return this.#cachedDesktopSlot;
  }

  /**
   * @returns {HTMLButtonElement | null}
   */
  #getDesktopStickyButton() {
    const slot = this.#getDesktopStickySlot();
    const button = slot?.querySelector('.product-information__desktop-sticky-atc__button');
    return button instanceof HTMLButtonElement ? button : null;
  }

  /**
   * @returns {HTMLButtonElement | null}
   */
  #ensureDesktopStickyButton() {
    const slot = this.#getDesktopStickySlot();
    if (!(slot instanceof HTMLElement)) return null;

    const existingButton = slot.querySelector('.product-information__desktop-sticky-atc__button');
    if (existingButton instanceof HTMLButtonElement) return existingButton;

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'product-information__desktop-sticky-atc__button add-to-cart-button button button--primary';

    const text = document.createElement('span');
    text.className = 'add-to-cart-text';

    const content = document.createElement('span');
    content.className = 'add-to-cart-text__content';

    const contentText = document.createElement('span');
    contentText.className = 'add-to-cart-text__content-text';
    contentText.textContent = 'Add to bag';

    content.appendChild(contentText);
    text.appendChild(content);
    button.appendChild(text);

    button.addEventListener('click', this.handleAddToCartClick, { signal: this.#abortController.signal });
    slot.appendChild(button);

    return button;
  }

  /**
   * @param {boolean} needsSelection
   * @param {boolean} isButtonDisabled
   * @param {boolean} isUnavailable
   */
  #syncDesktopStickyButtonState(needsSelection, isButtonDisabled, isUnavailable) {
    const button = this.#getDesktopStickyButton();
    if (!(button instanceof HTMLButtonElement)) return;

    button.disabled = needsSelection ? false : isButtonDisabled || Boolean(isUnavailable);
    if (isUnavailable && !needsSelection) {
      button.dataset.unavailable = 'true';
    } else {
      delete button.dataset.unavailable;
    }
  }

  #updateDesktopSlotGeometry() {
    const slot = this.#getDesktopStickySlot();
    if (!(slot instanceof HTMLElement)) return;

    const detailsColumn = slot.closest('.product-information__details-desktop');
    if (!(detailsColumn instanceof HTMLElement)) return;

    const rect = detailsColumn.getBoundingClientRect();
    if (rect.width <= 0) return;

    slot.style.setProperty('--desktop-sticky-atc-left', `${rect.left}px`);
    slot.style.setProperty('--desktop-sticky-atc-width', `${rect.width}px`);
    this.refs.stickyBar.style.setProperty('--desktop-sticky-atc-width', `${rect.width}px`);
  }

  /**
   * @param {boolean} show
   */
  #updateDesktopStickySlotVisibility(show) {
    const slot = this.#getDesktopStickySlot();
    if (!(slot instanceof HTMLElement)) return;

    if (show) {
      slot.hidden = false;
      slot.setAttribute('aria-hidden', 'false');
      return;
    }

    slot.hidden = true;
    slot.setAttribute('aria-hidden', 'true');
  }

  #syncInitialBundleStickyState = () => {
    if (!this.#isBundlePage()) return;

    const targetButton = this.#resolveTargetAddToCartButton(this.#getProductForm());
    if (targetButton instanceof HTMLButtonElement) {
      this.#targetAddToCartButton = targetButton;
    }

    // Ask bundle-buy-buttons to re-broadcast the authoritative bundle state
    // instead of inferring readiness from the button's disabled attribute
    // (the bundle add button is intentionally never disabled).
    document.dispatchEvent(new CustomEvent('bundle:sticky-state:request', { bubbles: true }));
  };

  #applyBundleStickyLoadingState() {
    this.#setBundleStickyButtonsLoading(true);
    this.#setStickyButtonLabel('Loading...');
    this.#setBundleStickyPrice('');
  }

  /**
   * @param {boolean} isLoading
   */
  #setBundleStickyButtonsLoading(isLoading) {
    this.#ensureDesktopStickyButton();

    for (const button of [this.refs.addToCartButton, this.#getDesktopStickyButton()]) {
      if (!(button instanceof HTMLButtonElement)) continue;
      button.classList.toggle('is-loading', isLoading);
      button.disabled = isLoading;
      if (isLoading) {
        button.setAttribute('aria-busy', 'true');
      } else {
        button.removeAttribute('aria-busy');
      }
    }
  }

  /**
   * @param {boolean} ready
   * @param {string} [formattedTotal]
   */
  #applyBundleStickyReadyState(ready, formattedTotal = '') {
    this.#setBundleStickyButtonsLoading(false);

    const label = ready ? 'Add to bag' : 'Select options';
    const mobileLabel = this.#ensureMobileButtonLabel();

    this.dataset.needsSelection = ready ? 'false' : 'true';
    this.dataset.variantAvailable = ready && mobileLabel instanceof HTMLElement ? 'true' : 'false';
    this.#setBundleStickyPrice(ready ? formattedTotal : '');

    // On bundle pages no other code path re-syncs the button's disabled
    // state (variant:update is stopped at the bundle root in full mode), so
    // apply it here. Keep the button clickable when selection is needed so
    // the click can scroll to the picker.
    if (this.refs.addToCartButton instanceof HTMLButtonElement) {
      this.refs.addToCartButton.disabled = false;
      delete this.refs.addToCartButton.dataset.unavailable;
    }
    const desktopStickyButton = this.#getDesktopStickyButton();
    if (desktopStickyButton instanceof HTMLButtonElement) {
      desktopStickyButton.disabled = false;
      delete desktopStickyButton.dataset.unavailable;
    }

    if (mobileLabel instanceof HTMLElement) {
      mobileLabel.textContent = label;
      mobileLabel.hidden = window.innerWidth >= 1024 && !ready;
    }

    this.#setStickyButtonLabel(label);
  }

  /**
   * @param {string} formattedTotal
   */
  #setBundleStickyPrice(formattedTotal) {
    const priceText = formattedTotal.trim();
    const desktopPrice = this.#ensureBundleDesktopPriceElement();
    const priceElements = Array.from(this.querySelectorAll('.sticky-add-to-cart__price'));

    if (!priceText) {
      priceElements.forEach((element) => {
        if (element instanceof HTMLElement && element.dataset.bundleStickyPrice === 'true') {
          element.hidden = true;
        }
      });
      return;
    }

    priceElements.forEach((element) => {
      if (!(element instanceof HTMLElement)) return;
      if (element === desktopPrice || element.classList.contains('sticky-add-to-cart__price--mobile')) {
        element.hidden = false;
        element.dataset.bundleStickyPrice = 'true';
        element.replaceChildren(this.#buildBundleStickyPriceNode(priceText));
      }
    });
  }

  /**
   * @param {string} priceText
   * @returns {HTMLElement}
   */
  #buildBundleStickyPriceNode(priceText) {
    const wrapper = document.createElement('span');
    const price = document.createElement('span');

    wrapper.className = 'price-group-wrapper';
    price.className = 'price';
    price.textContent = priceText;
    wrapper.append(price);

    return wrapper;
  }

  #ensureBundleDesktopPriceElement() {
    const actions = this.querySelector('.sticky-add-to-cart__bar-actions');
    if (!(actions instanceof HTMLElement)) return null;

    let price = actions.querySelector('.sticky-add-to-cart__price--desktop');
    if (price instanceof HTMLElement) return price;

    const newPrice = document.createElement('div');
    newPrice.className = 'sticky-add-to-cart__price sticky-add-to-cart__price--desktop';
    newPrice.dataset.testid = 'sticky-price-display';
    newPrice.dataset.bundleStickyPrice = 'true';
    actions.insertBefore(newPrice, this.refs.addToCartButton);
    return newPrice;
  }

  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;
    const available = !addToCartButton.disabled;

    quantityNumber.textContent = this.#currentQuantity.toString();
    quantityDisplay.style.display = available && this.#currentQuantity > 1 ? 'inline' : 'none';
  }

  /**
   * @param {string} label
   */
  #setStickyButtonLabel(label) {
    const textTargets = [
      this.refs.addToCartButton?.querySelector('.add-to-cart-text__content-text'),
      this.#getDesktopStickyButton()?.querySelector('.add-to-cart-text__content-text'),
    ];

    for (const textTarget of textTargets) {
      if (textTarget instanceof HTMLElement) {
        textTarget.textContent = label;
      }
    }
  }

  #scrollToVariantPicker() {
    const productSection = this.#getProductSection();
    if (!productSection) return;

    const bundlePicker = productSection.querySelector('[data-bundle-picker]');
    if (bundlePicker instanceof HTMLElement) {
      const pickers = Array.from(bundlePicker.querySelectorAll('variant-picker-v2, variant-picker'));
      const picker =
        pickers.find((element) => this.#isVisibleElement(element)) ?? pickers[0];
      if (picker instanceof HTMLElement) {
        const headerOffset = this.#getHeaderOffset();
        const top = window.scrollY + picker.getBoundingClientRect().top - headerOffset - 12;
        window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
        return;
      }
    }

    const variantPickers = Array.from(
      productSection.querySelectorAll('variant-picker-v2, variant-picker')
    );
    const variantPicker =
      variantPickers.find((picker) => this.#isVisibleElement(picker)) ?? variantPickers[0];
    if (!(variantPicker instanceof HTMLElement)) return;

    const headerOffset = this.#getHeaderOffset();
    const top = window.scrollY + variantPicker.getBoundingClientRect().top - headerOffset - 12;
    window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getProductSection() {
    if (this.#cachedProductSection?.isConnected) {
      return this.#cachedProductSection;
    }

    const productInformation =
      document.querySelector('[data-testid="product-information"]') ||
      document.querySelector('.product-information');

    this.#cachedProductSection = productInformation?.closest('.shopify-section') ?? null;
    return this.#cachedProductSection;
  }

  /**
   * @returns {boolean} Whether the current product section is a bundle page.
   */
  #isBundlePage() {
    return Boolean(this.#getProductSection()?.querySelector('[data-bundle-buy-buttons]'));
  }

  /**
   * @returns {HTMLElement | null}
   */
  #findLiveProductForm() {
    const productSection = this.#getProductSection();
    if (productSection) {
      const form = productSection.querySelector('product-form-component[data-product-id]');
      if (form instanceof HTMLElement) return form;
    }

    const scopedForm = document.querySelector(
      '[data-testid="product-information"] product-form-component[data-product-id], .product-information product-form-component[data-product-id]'
    );
    return scopedForm instanceof HTMLElement ? scopedForm : null;
  }

  /**
   * Keeps sticky data-product-id aligned with the live PDP form (e.g. after color switches).
   */
  #reconcileProductIdFromLiveDom() {
    if (this.#lastReconcileGeneration === this.#syncGeneration) return;
    this.#lastReconcileGeneration = this.#syncGeneration;

    const liveForm = this.#findLiveProductForm();
    const liveProductId = liveForm?.dataset.productId;
    if (liveProductId) {
      this.dataset.productId = liveProductId;
    }
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    this.#reconcileProductIdFromLiveDom();

    const liveForm = this.#findLiveProductForm();
    if (liveForm instanceof HTMLElement) return liveForm;

    const productId = this.dataset.productId;
    if (!productId) return null;

    return document.querySelector(`product-form-component[data-product-id="${productId}"]`);
  }

  /**
   * @param {Element} scope
   * @returns {HTMLElement | null}
   */
  #findProductFormInScope(scope) {
    const liveForm = scope.querySelector('product-form-component[data-product-id]');
    if (liveForm instanceof HTMLElement) return liveForm;

    const productId = this.dataset.productId;
    if (!productId) return null;
    return scope.querySelector(`product-form-component[data-product-id="${productId}"]`);
  }

  /**
   * @returns {HTMLElement | null}
   */
  #findStaticAddToCartButton() {
    const productSection = this.#getProductSection();
    if (!productSection) return null;

    const detailsSelector =
      window.innerWidth < 750
        ? '.product-information__details-mobile-body'
        : '.product-information__details-desktop';

    const productInformation = productSection.querySelector('.product-information');
    const detailsContainer =
      productInformation?.querySelector(detailsSelector) || productInformation || productSection;

    const button = detailsContainer?.querySelector('[ref="addToCartButton"]');
    return button instanceof HTMLButtonElement ? button : null;
  }

  /**
   * @param {HTMLElement | null} fallbackProductForm
   * @returns {HTMLButtonElement | null}
   */
  #resolveTargetAddToCartButton(fallbackProductForm) {
    const productSection = this.#getProductSection();
    if (!productSection) return null;

    const bundleButtons = Array.from(
      productSection.querySelectorAll('.buy-buttons-block[data-bundle-buy-buttons] .add-to-cart-button')
    ).filter((button) => button instanceof HTMLButtonElement);

    const visibleBundleButton = bundleButtons.find((button) => this.#isVisibleElement(button));
    if (visibleBundleButton instanceof HTMLButtonElement) return visibleBundleButton;

    const firstBundleButton = bundleButtons[0];
    if (firstBundleButton instanceof HTMLButtonElement) return firstBundleButton;

    if (!(fallbackProductForm instanceof HTMLElement)) return null;
    const defaultButton = fallbackProductForm.querySelector('[ref="addToCartButton"]');
    return defaultButton instanceof HTMLButtonElement ? defaultButton : null;
  }

  /**
   * @param {Element} element
   * @returns {boolean}
   */
  #isVisibleElement(element) {
    if (!(element instanceof HTMLElement)) return false;
    if (element.hidden || element.closest('[hidden], [inert]')) return false;
    if (element.offsetWidth === 0 && element.offsetHeight === 0) return false;

    const style = window.getComputedStyle(element);
    if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') return false;

    const rect = element.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  }

  /**
   * @returns {number}
   */
  #getHeaderOffset() {
    if (this.#cachedHeaderOffset != null) {
      return this.#cachedHeaderOffset;
    }

    const rootStyles = getComputedStyle(document.documentElement);
    const headerVar = parseFloat(rootStyles.getPropertyValue('--header-group-height'));
    if (!Number.isNaN(headerVar) && headerVar > 0) {
      this.#cachedHeaderOffset = headerVar;
      return headerVar;
    }

    const stickyHeader = document.querySelector('#header-group #header-component');
    if (stickyHeader instanceof HTMLElement) {
      this.#cachedHeaderOffset = stickyHeader.getBoundingClientRect().height;
      return this.#cachedHeaderOffset;
    }

    this.#cachedHeaderOffset = 0;
    return 0;
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
